/**
 * RegistrationService
 * Service for managing course registrations with list/detail views, session updates
 * and facilitator assignment
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for registration CRUD operations, paged list views, detail views,
 * session progress updates and facilitator assignment
 */
export class RegistrationService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? RegistrationService.DEFAULT_SERVICE_PATH,
            basePath: RegistrationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Soft-delete many registrations off the list in one call (spam/junk cleanup). Missing ones are skipped
     * server-side, so this reports partial success honestly. Gated by AllowBulkDelete.Registrations (default OFF)
     * and restricted to ActsCareAdmin — a 403 means the setting is off. `delete(id)` (inherited) removes one.
     * POST api/registrations/delete-many
     * @param ids - The registration IDs to delete
     * @returns How many registrations were actually removed
     */
    async deleteMany(ids) {
        const response = await this.deleteManyRaw(ids);
        return response.data?.removed ?? 0;
    }
    deleteManyRaw(ids) {
        const url = this.buildUrl('delete-many');
        return this.http.post(url, { ids });
    }
    /**
     * Get the paged registration list view (filtered)
     * @param filter - Optional filter parameters
     * @returns Paged registration list view response
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve registration list view');
        }
        return response.data;
    }
    /**
     * Get the paged registration list view (filtered) with full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with paged registration list view response
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter ?? {});
    }
    // ---------------------------------------------------------------------------
    // Overview aggregates (admin) — registrations by course / location, monthly trend
    // ---------------------------------------------------------------------------
    /**
     * Get active/total registration counts grouped by course
     * @returns Array of course breakdown buckets, descending by activeCount
     */
    async statsByCourse() {
        const response = await this.statsByCourseRaw();
        return response.data ?? [];
    }
    /**
     * Get active/total registration counts grouped by course with full response
     * @returns ResponseData with array of course breakdown buckets
     */
    statsByCourseRaw() {
        const url = this.buildUrl('stats/by-course');
        return this.http.get(url);
    }
    /**
     * Get active/total registration counts grouped by location (Remote = id 0)
     * @returns Array of location breakdown buckets, descending by activeCount
     */
    async statsByLocation() {
        const response = await this.statsByLocationRaw();
        return response.data ?? [];
    }
    /**
     * Get active/total registration counts grouped by location with full response
     * @returns ResponseData with array of location breakdown buckets
     */
    statsByLocationRaw() {
        const url = this.buildUrl('stats/by-location');
        return this.http.get(url);
    }
    /**
     * Get the monthly started-vs-completed registration trend
     * @param months - Number of months to include, most recent last (default 12)
     * @returns Array of monthly trend rows, oldest first
     */
    async statsMonthly(months = 12) {
        const response = await this.statsMonthlyRaw(months);
        return response.data ?? [];
    }
    /**
     * Get the monthly started-vs-completed registration trend with full response
     * @param months - Number of months to include (default 12)
     * @returns ResponseData with array of monthly trend rows
     */
    statsMonthlyRaw(months = 12) {
        const params = new URLSearchParams();
        params.append('months', months.toString());
        const url = this.buildUrl(`stats/monthly?${params.toString()}`);
        return this.http.get(url);
    }
    /**
     * Get registration detail view by ID (participants, facilitators, sessions, custom fields)
     * @param id - The registration ID
     * @returns Registration model view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Registration view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get registration detail view by ID with full response
     * @param id - The registration ID
     * @returns ResponseData with registration model view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get all registrations for a course
     * @param courseId - The course ID
     * @returns Array of registrations
     */
    async byCourse(courseId) {
        const response = await this.byCourseRaw(courseId);
        return response.data ?? [];
    }
    /**
     * Get all registrations for a course with full response
     * @param courseId - The course ID
     * @returns ResponseData with array of registrations
     */
    byCourseRaw(courseId) {
        const url = this.buildUrl(`by-course/${courseId}`);
        return this.http.get(url);
    }
    /**
     * Get all registrations for an intake date
     * @param dateId - The course date ID
     * @returns Array of registrations
     */
    async byDate(dateId) {
        const response = await this.byDateRaw(dateId);
        return response.data ?? [];
    }
    /**
     * Get all registrations for an intake date with full response
     * @param dateId - The course date ID
     * @returns ResponseData with array of registrations
     */
    byDateRaw(dateId) {
        const url = this.buildUrl(`by-date/${dateId}`);
        return this.http.get(url);
    }
    /**
     * Update a registration session (status / progress / date)
     * @param registrationSessionId - The registration session ID
     * @param data - Update payload
     * @returns Updated registration session model view
     */
    async updateSession(registrationSessionId, data) {
        const response = await this.updateSessionRaw(registrationSessionId, data);
        if (!response.data) {
            throw new Error(`Failed to update registration session with ID ${registrationSessionId}`);
        }
        return response.data;
    }
    /**
     * Update a registration session with full response
     * @param registrationSessionId - The registration session ID
     * @param data - Update payload
     * @returns ResponseData with updated registration session model view
     */
    updateSessionRaw(registrationSessionId, data) {
        const url = this.buildUrl(`sessions/${registrationSessionId}`);
        return this.http.put(url, data);
    }
    /**
     * Assign a facilitator to a registration
     * @param registrationId - The registration ID
     * @param userId - The facilitator user ID
     * @param isLead - Whether the facilitator is the designated lead
     * @returns True if the assignment succeeded
     */
    async assignFacilitator(registrationId, userId, isLead) {
        const response = await this.assignFacilitatorRaw(registrationId, userId, isLead);
        return response.success;
    }
    /**
     * Assign a facilitator to a registration with full response
     * @param registrationId - The registration ID
     * @param userId - The facilitator user ID
     * @param isLead - Whether the facilitator is the designated lead
     * @returns ResponseData (success/fail)
     */
    assignFacilitatorRaw(registrationId, userId, isLead) {
        const url = this.buildUrlWithParams(`${registrationId}/facilitators/${userId}`, {
            isLead,
        });
        return this.http.post(url, {});
    }
    /**
     * Remove a facilitator from a registration
     * @param registrationId - The registration ID
     * @param userId - The facilitator user ID
     * @returns True if the removal succeeded
     */
    async removeFacilitator(registrationId, userId) {
        const response = await this.removeFacilitatorRaw(registrationId, userId);
        return response.success;
    }
    /**
     * Remove a facilitator from a registration with full response
     * @param registrationId - The registration ID
     * @param userId - The facilitator user ID
     * @returns ResponseData (success/fail)
     */
    removeFacilitatorRaw(registrationId, userId) {
        const url = this.buildUrl(`${registrationId}/facilitators/${userId}`);
        return this.http.delete(url);
    }
    /**
     * Get the facilitators assigned to a registration (enriched name + avatar)
     * @param registrationId - The registration ID
     * @returns Array of registration facilitator views
     */
    async getFacilitators(registrationId) {
        const response = await this.getFacilitatorsRaw(registrationId);
        return response.data ?? [];
    }
    /**
     * Get the facilitators assigned to a registration with full response
     * @param registrationId - The registration ID
     * @returns ResponseData with array of registration facilitator views
     */
    getFacilitatorsRaw(registrationId) {
        const url = this.buildUrl(`${registrationId}/facilitators`);
        return this.http.get(url);
    }
    // ---------------------------------------------------------------------------
    // Members (MemberRegistration links)
    // ---------------------------------------------------------------------------
    /** Get the members (participants) on a registration. */
    async getMembers(registrationId) {
        const response = await this.getMembersRaw(registrationId);
        return response.data ?? [];
    }
    getMembersRaw(registrationId) {
        const url = this.buildUrl(`${registrationId}/members`);
        return this.http.get(url);
    }
    /** Add a member (participant) to a registration. */
    async addMember(registrationId, memberId, isMain) {
        const response = await this.addMemberRaw(registrationId, memberId, isMain);
        if (!response.data) {
            throw new Error(`Failed to add member ${memberId} to registration ${registrationId}`);
        }
        return response.data;
    }
    addMemberRaw(registrationId, memberId, isMain) {
        const url = this.buildUrlWithParams(`${registrationId}/members/${memberId}`, { isMain });
        return this.http.post(url, {});
    }
    /** Remove a member (participant) from a registration. */
    async removeMember(registrationId, memberId) {
        const response = await this.removeMemberRaw(registrationId, memberId);
        return response.success;
    }
    removeMemberRaw(registrationId, memberId) {
        const url = this.buildUrl(`${registrationId}/members/${memberId}`);
        return this.http.delete(url);
    }
    /**
     * Repair: re-import participants this registration is missing from its originating intake (when approval
     * failed to carry someone across). Each is match-or-created from the intake participant's FULL details
     * (contacts, gender, DOB, member status) server-side — not just their name. Idempotent; participants
     * already on the registration are skipped.
     * @param registrationId - The registration ID
     * @param participantIDs - Optional subset of participant IDs to import; omit to import every missing one
     * @returns The number of participants imported
     */
    async importFromIntake(registrationId, participantIDs, includeApplicant) {
        const response = await this.importFromIntakeRaw(registrationId, participantIDs, includeApplicant);
        return response.data ?? 0;
    }
    /**
     * Re-import missing participants from the originating intake, with full response.
     * @param registrationId - The registration ID
     * @param participantIDs - Optional subset of participant IDs to import
     * @param includeApplicant - Also import the applicant from the intake header (who has no participant id),
     *   badged as the applicant — covers intakes whose participant list is empty
     * @returns ResponseData with the number imported
     */
    importFromIntakeRaw(registrationId, participantIDs, includeApplicant) {
        const url = this.buildUrl(`${registrationId}/import-from-intake`);
        return this.http.post(url, { participantIDs: participantIDs ?? null, includeApplicant: includeApplicant ?? false });
    }
    // ---------------------------------------------------------------------------
    // Notes
    // ---------------------------------------------------------------------------
    /** Get the audit-trail notes on a registration. */
    async getNotes(registrationId) {
        const response = await this.getNotesRaw(registrationId);
        return response.data ?? [];
    }
    getNotesRaw(registrationId) {
        const url = this.buildUrl(`${registrationId}/notes`);
        return this.http.get(url);
    }
    /** Add a note to a registration. */
    async addNote(registrationId, data) {
        const response = await this.addNoteRaw(registrationId, data);
        if (!response.data) {
            throw new Error(`Failed to add note to registration ${registrationId}`);
        }
        return response.data;
    }
    addNoteRaw(registrationId, data) {
        const url = this.buildUrl(`${registrationId}/notes`);
        return this.http.post(url, data);
    }
    /** Update a registration note. */
    async updateNote(registrationId, noteId, data) {
        const response = await this.updateNoteRaw(registrationId, noteId, data);
        if (!response.data) {
            throw new Error(`Failed to update note ${noteId} on registration ${registrationId}`);
        }
        return response.data;
    }
    updateNoteRaw(registrationId, noteId, data) {
        const url = this.buildUrl(`${registrationId}/notes/${noteId}`);
        return this.http.put(url, data);
    }
    /** Delete a registration note. */
    async deleteNote(registrationId, noteId) {
        const response = await this.deleteNoteRaw(registrationId, noteId);
        return response.success;
    }
    deleteNoteRaw(registrationId, noteId) {
        const url = this.buildUrl(`${registrationId}/notes/${noteId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Session notes (scoped to a single RegistrationSession sitting)
    // ---------------------------------------------------------------------------
    /** Get the audit-trail notes on a registration session (author-enriched). */
    async getSessionNotes(registrationSessionId) {
        const response = await this.getSessionNotesRaw(registrationSessionId);
        return response.data ?? [];
    }
    getSessionNotesRaw(registrationSessionId) {
        const url = this.buildUrl(`sessions/${registrationSessionId}/notes`);
        return this.http.get(url);
    }
    /** Add a note to a registration session. Returns the author-enriched view. */
    async addSessionNote(registrationSessionId, data) {
        const response = await this.addSessionNoteRaw(registrationSessionId, data);
        if (!response.data) {
            throw new Error(`Failed to add note to registration session ${registrationSessionId}`);
        }
        return response.data;
    }
    addSessionNoteRaw(registrationSessionId, data) {
        const url = this.buildUrl(`sessions/${registrationSessionId}/notes`);
        return this.http.post(url, data);
    }
    /** Update a registration session note. Returns the author-enriched view. */
    async updateSessionNote(registrationSessionId, sessionNoteId, data) {
        const response = await this.updateSessionNoteRaw(registrationSessionId, sessionNoteId, data);
        if (!response.data) {
            throw new Error(`Failed to update session note ${sessionNoteId} on session ${registrationSessionId}`);
        }
        return response.data;
    }
    updateSessionNoteRaw(registrationSessionId, sessionNoteId, data) {
        const url = this.buildUrl(`sessions/${registrationSessionId}/notes/${sessionNoteId}`);
        return this.http.put(url, data);
    }
    /** Delete a registration session note. */
    async deleteSessionNote(registrationSessionId, sessionNoteId) {
        const response = await this.deleteSessionNoteRaw(registrationSessionId, sessionNoteId);
        return response.success;
    }
    deleteSessionNoteRaw(registrationSessionId, sessionNoteId) {
        const url = this.buildUrl(`sessions/${registrationSessionId}/notes/${sessionNoteId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Files (registration/cohort-scoped)
    // ---------------------------------------------------------------------------
    /** Get the files attached to a registration/cohort. */
    async getFiles(registrationId) {
        const response = await this.getFilesRaw(registrationId);
        return response.data ?? [];
    }
    getFilesRaw(registrationId) {
        const url = this.buildUrl(`${registrationId}/files`);
        return this.http.get(url);
    }
    /** Attach a file to a registration/cohort. */
    async addFile(registrationId, data) {
        const response = await this.addFileRaw(registrationId, data);
        if (!response.data) {
            throw new Error(`Failed to add file to registration ${registrationId}`);
        }
        return response.data;
    }
    addFileRaw(registrationId, data) {
        const url = this.buildUrl(`${registrationId}/files`);
        return this.http.post(url, data);
    }
    /** Delete a registration file. */
    async deleteFile(registrationId, fileId) {
        const response = await this.deleteFileRaw(registrationId, fileId);
        return response.success;
    }
    deleteFileRaw(registrationId, fileId) {
        const url = this.buildUrl(`${registrationId}/files/${fileId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Sessions (updateSession already exists above)
    // ---------------------------------------------------------------------------
    /** Get a registration's scheduled sessions (enriched views). */
    async getSessions(registrationId) {
        const response = await this.getSessionsRaw(registrationId);
        return response.data ?? [];
    }
    getSessionsRaw(registrationId) {
        return this.http.get(this.buildUrl(`${registrationId}/sessions`));
    }
    /** Get a single registration session (enriched view). */
    async getSession(registrationSessionId) {
        const response = await this.getSessionRaw(registrationSessionId);
        if (!response.data)
            throw new Error(`Registration session ${registrationSessionId} not found`);
        return response.data;
    }
    getSessionRaw(registrationSessionId) {
        return this.http.get(this.buildUrl(`sessions/${registrationSessionId}`));
    }
    /** Add a scheduled session to a registration. */
    async addSession(registrationId, data) {
        const response = await this.addSessionRaw(registrationId, data);
        if (!response.data)
            throw new Error(`Failed to add session to registration ${registrationId}`);
        return response.data;
    }
    addSessionRaw(registrationId, data) {
        return this.http.post(this.buildUrl(`${registrationId}/sessions`), data);
    }
    /** Delete a registration session. */
    async deleteSession(registrationSessionId) {
        const response = await this.deleteSessionRaw(registrationSessionId);
        return response.success;
    }
    deleteSessionRaw(registrationSessionId) {
        return this.http.delete(this.buildUrl(`sessions/${registrationSessionId}`));
    }
    // ---------------------------------------------------------------------------
    // Course custom data (captured course-level custom-field values)
    // ---------------------------------------------------------------------------
    /** Get a registration's captured course custom-field values (with depth). */
    async getCourseCustomData(registrationId) {
        const response = await this.getCourseCustomDataRaw(registrationId);
        return response.data ?? [];
    }
    getCourseCustomDataRaw(registrationId) {
        return this.http.get(this.buildUrl(`${registrationId}/course-custom-data`));
    }
    /** Get a single captured course custom-field value (with depth). */
    async getCourseCustomDataItem(dataId) {
        const response = await this.getCourseCustomDataItemRaw(dataId);
        if (!response.data)
            throw new Error(`Course custom data ${dataId} not found`);
        return response.data;
    }
    getCourseCustomDataItemRaw(dataId) {
        return this.http.get(this.buildUrl(`course-custom-data/${dataId}`));
    }
    /** Capture/replace a single course custom-field value on a registration. */
    async addCourseCustomData(registrationId, data) {
        const response = await this.addCourseCustomDataRaw(registrationId, data);
        if (!response.data)
            throw new Error(`Failed to add course custom data to registration ${registrationId}`);
        return response.data;
    }
    addCourseCustomDataRaw(registrationId, data) {
        return this.http.post(this.buildUrl(`${registrationId}/course-custom-data`), data);
    }
    /** Bulk upsert a registration's course custom-field values (a whole-form save). */
    async upsertCourseCustomData(registrationId, items) {
        const response = await this.upsertCourseCustomDataRaw(registrationId, items);
        return response.data ?? [];
    }
    upsertCourseCustomDataRaw(registrationId, items) {
        return this.http.put(this.buildUrl(`${registrationId}/course-custom-data`), items);
    }
    /** Update a captured course custom-field value. */
    async updateCourseCustomData(dataId, data) {
        const response = await this.updateCourseCustomDataRaw(dataId, data);
        if (!response.data)
            throw new Error(`Failed to update course custom data ${dataId}`);
        return response.data;
    }
    updateCourseCustomDataRaw(dataId, data) {
        return this.http.put(this.buildUrl(`course-custom-data/${dataId}`), data);
    }
    /** Delete a captured course custom-field value. */
    async deleteCourseCustomData(dataId) {
        const response = await this.deleteCourseCustomDataRaw(dataId);
        return response.success;
    }
    deleteCourseCustomDataRaw(dataId) {
        return this.http.delete(this.buildUrl(`course-custom-data/${dataId}`));
    }
    // ---------------------------------------------------------------------------
    // Session custom data (captured special-session custom-field values)
    // ---------------------------------------------------------------------------
    /** Get a registration session's captured special-session custom-field values (with depth). */
    async getCustomSessionData(registrationSessionId) {
        const response = await this.getCustomSessionDataRaw(registrationSessionId);
        return response.data ?? [];
    }
    getCustomSessionDataRaw(registrationSessionId) {
        return this.http.get(this.buildUrl(`sessions/${registrationSessionId}/custom-data`));
    }
    /** Get a single captured special-session custom-field value (with depth). */
    async getCustomSessionDataItem(dataId) {
        const response = await this.getCustomSessionDataItemRaw(dataId);
        if (!response.data)
            throw new Error(`Session custom data ${dataId} not found`);
        return response.data;
    }
    getCustomSessionDataItemRaw(dataId) {
        return this.http.get(this.buildUrl(`session-custom-data/${dataId}`));
    }
    /** Capture/replace a single special-session custom-field value on a registration session. */
    async addCustomSessionData(registrationSessionId, data) {
        const response = await this.addCustomSessionDataRaw(registrationSessionId, data);
        if (!response.data)
            throw new Error(`Failed to add session custom data to session ${registrationSessionId}`);
        return response.data;
    }
    addCustomSessionDataRaw(registrationSessionId, data) {
        return this.http.post(this.buildUrl(`sessions/${registrationSessionId}/custom-data`), data);
    }
    /** Bulk upsert a session's special-session custom-field values (a whole-form save). */
    async upsertCustomSessionData(registrationSessionId, items) {
        const response = await this.upsertCustomSessionDataRaw(registrationSessionId, items);
        return response.data ?? [];
    }
    upsertCustomSessionDataRaw(registrationSessionId, items) {
        return this.http.put(this.buildUrl(`sessions/${registrationSessionId}/custom-data`), items);
    }
    /** Update a captured special-session custom-field value. */
    async updateCustomSessionData(dataId, data) {
        const response = await this.updateCustomSessionDataRaw(dataId, data);
        if (!response.data)
            throw new Error(`Failed to update session custom data ${dataId}`);
        return response.data;
    }
    updateCustomSessionDataRaw(dataId, data) {
        return this.http.put(this.buildUrl(`session-custom-data/${dataId}`), data);
    }
    /** Delete a captured special-session custom-field value. */
    async deleteCustomSessionData(dataId) {
        const response = await this.deleteCustomSessionDataRaw(dataId);
        return response.success;
    }
    deleteCustomSessionDataRaw(dataId) {
        return this.http.delete(this.buildUrl(`session-custom-data/${dataId}`));
    }
    // ---------------------------------------------------------------------------
    // Participant details (contact snapshots)
    // ---------------------------------------------------------------------------
    /** Get a registration's participant detail rows. */
    async getDetails(registrationId) {
        const response = await this.getDetailsRaw(registrationId);
        return response.data ?? [];
    }
    getDetailsRaw(registrationId) {
        return this.http.get(this.buildUrl(`${registrationId}/details`));
    }
    /** Add a participant detail to a registration. */
    async addDetail(registrationId, data) {
        const response = await this.addDetailRaw(registrationId, data);
        if (!response.data)
            throw new Error(`Failed to add detail to registration ${registrationId}`);
        return response.data;
    }
    addDetailRaw(registrationId, data) {
        return this.http.post(this.buildUrl(`${registrationId}/details`), data);
    }
    /** Update a participant detail. */
    async updateDetail(registrationId, detailId, data) {
        const response = await this.updateDetailRaw(registrationId, detailId, data);
        if (!response.data)
            throw new Error(`Failed to update detail ${detailId}`);
        return response.data;
    }
    updateDetailRaw(registrationId, detailId, data) {
        return this.http.put(this.buildUrl(`${registrationId}/details/${detailId}`), data);
    }
    /** Delete a participant detail. */
    async deleteDetail(registrationId, detailId) {
        const response = await this.deleteDetailRaw(registrationId, detailId);
        return response.success;
    }
    deleteDetailRaw(registrationId, detailId) {
        return this.http.delete(this.buildUrl(`${registrationId}/details/${detailId}`));
    }
    /** Toggle a facilitator's lead flag on a registration. */
    async updateFacilitator(registrationId, userId, data) {
        const response = await this.updateFacilitatorRaw(registrationId, userId, data);
        if (!response.data)
            throw new Error(`Failed to update facilitator ${userId} on registration ${registrationId}`);
        return response.data;
    }
    updateFacilitatorRaw(registrationId, userId, data) {
        return this.http.put(this.buildUrl(`${registrationId}/facilitators/${userId}`), data);
    }
    // ---------------------------------------------------------------------------
    // Status-only changes (new status in the route, no body)
    // ---------------------------------------------------------------------------
    /** Change only the registration's status. */
    async changeStatus(registrationId, newStatus) {
        const response = await this.changeStatusRaw(registrationId, newStatus);
        if (!response.data)
            throw new Error(`Failed to change status for registration ${registrationId}`);
        return response.data;
    }
    changeStatusRaw(registrationId, newStatus) {
        return this.http.patch(this.buildUrl(`${registrationId}/change-status/${newStatus}`));
    }
    /** Change only a registration session's status. */
    async changeSessionStatus(registrationSessionId, newStatus) {
        const response = await this.changeSessionStatusRaw(registrationSessionId, newStatus);
        if (!response.data)
            throw new Error(`Failed to change status for session ${registrationSessionId}`);
        return response.data;
    }
    changeSessionStatusRaw(registrationSessionId, newStatus) {
        return this.http.patch(this.buildUrl(`sessions/${registrationSessionId}/change-status/${newStatus}`));
    }
    /** Change only a registration session's progress status. */
    async changeSessionProgress(registrationSessionId, newProgressStatus) {
        const response = await this.changeSessionProgressRaw(registrationSessionId, newProgressStatus);
        if (!response.data)
            throw new Error(`Failed to change progress for session ${registrationSessionId}`);
        return response.data;
    }
    changeSessionProgressRaw(registrationSessionId, newProgressStatus) {
        return this.http.patch(this.buildUrl(`sessions/${registrationSessionId}/change-progress/${newProgressStatus}`));
    }
}
RegistrationService.DEFAULT_SERVICE_PATH = 'ac-courses';
RegistrationService.BASE_PATH = 'api/registrations';
//# sourceMappingURL=RegistrationService.js.map